import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Calendar, Users, Activity, Satellite, Sun } from "lucide-react";
import { Navbar } from "@/components/hx/Navbar";
import { Footer } from "@/components/hx/Footer";
import { PageHeader } from "@/components/hx/PageHeader";
import { STRATEGIC_PROJECTS } from "@/data/hannonexus";
import { useHannoContent } from "@/data/hanno-content";
import { CountryFlag } from "@/components/hanno/Flags";

const NEON = "#0A9B5F";
const CYAN = "#06B6D4";
const SOLAR = "#F59E0B";

export default function ProjectsPage() {
  const c = useHannoContent();
  const dt = c.dashboards;
  return (
    <div className="App relative bg-hx-bg text-hx-text" data-testid="page-projects">
      <Navbar />
      <main>
        <PageHeader
          breadcrumb={c.nav.projects}
          eyebrow={c.projects.eyebrow}
          title={c.projects.title + " "}
          accent={c.projects.accent}
        />
        <section className="relative py-14 lg:py-20" data-testid="projects-grid">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">

            {/* ============================================================ */}
            {/*  FEATURED: BioPintoSTP · Live Dashboard                      */}
            {/* ============================================================ */}
            <motion.article
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative rounded-2xl border overflow-hidden mb-12 lg:mb-16"
              style={{
                borderColor: `${NEON}40`,
                boxShadow: `0 0 60px -20px ${NEON}55`,
              }}
              data-testid="featured-biopinto"
            >
              {/* Background image */}
              <div className="absolute inset-0 -z-0">
                <img
                  src="/hanno/biopinto/biopinto-hero.png"
                  alt="Vista aérea da quinta BioPintoSTP"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{
                  background: "linear-gradient(90deg, rgba(10,15,26,0.85) 0%, rgba(10,15,26,0.85) 45%, rgba(10,15,26,0.85) 100%)",
                }} />
              </div>

              {/* Ambient glow */}
              <div className="pointer-events-none absolute inset-0 opacity-60">
                <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl" style={{ background: `${CYAN}22` }} />
                <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full blur-3xl" style={{ background: `${NEON}22` }} />
              </div>

              <div className="relative grid lg:grid-cols-[1.15fr_1fr] gap-0">
                {/* Left: content */}
                <div className="p-8 lg:p-12">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ background: `${NEON}10`, borderColor: `${NEON}44` }}>
                      <span className="relative flex items-center justify-center w-2 h-2">
                        <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: NEON }} />
                        <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: NEON }} />
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: NEON }}>
                        {dt.common.live_dashboard}
                      </span>
                    </div>
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded border" style={{ background: `${CYAN}0d`, borderColor: `${CYAN}33` }}>
                      <Satellite size={11} style={{ color: CYAN }} strokeWidth={1.8} />
                      <span className="text-[9px] uppercase tracking-[0.24em] font-mono font-semibold" style={{ color: CYAN }}>
                        {dt.biopinto.featured_tag}
                      </span>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center gap-3">
                    <CountryFlag slug="sao-tome" className="w-11 h-8 rounded shadow-lg" />
                    <div className="text-[10px] uppercase tracking-[0.28em] text-hx-muted font-mono">
                      {dt.biopinto.country_label}
                    </div>
                  </div>

                  <h3 className="mt-4 font-display font-bold text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.05]">
                    {dt.biopinto.title_a} <span style={{ color: NEON }}>—</span> {dt.biopinto.title_b}
                  </h3>

                  <p className="mt-5 text-[14px] lg:text-[15px] text-hx-muted leading-relaxed max-w-xl">
                    {dt.biopinto.featured_lede}
                  </p>

                  {/* Quick KPIs */}
                  <div className="mt-8 grid grid-cols-3 gap-3">
                    <div className="rounded-lg px-3 py-3 border" style={{ background: `${SOLAR}08`, borderColor: `${SOLAR}22` }}>
                      <Sun size={13} style={{ color: SOLAR }} strokeWidth={1.7} className="mb-1.5" />
                      <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted font-mono">Solar</div>
                      <div className="mt-0.5 font-display font-bold text-lg tabular-nums" style={{ color: SOLAR }}>45 kW</div>
                    </div>
                    <div className="rounded-lg px-3 py-3 border" style={{ background: `${CYAN}08`, borderColor: `${CYAN}22` }}>
                      <Activity size={13} style={{ color: CYAN }} strokeWidth={1.7} className="mb-1.5" />
                      <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted font-mono">Poupança H₂O</div>
                      <div className="mt-0.5 font-display font-bold text-lg tabular-nums" style={{ color: CYAN }}>30 %</div>
                    </div>
                    <div className="rounded-lg px-3 py-3 border" style={{ background: `${NEON}08`, borderColor: `${NEON}22` }}>
                      <Satellite size={13} style={{ color: NEON }} strokeWidth={1.7} className="mb-1.5" />
                      <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted font-mono">Sensores</div>
                      <div className="mt-0.5 font-display font-bold text-lg tabular-nums" style={{ color: NEON }}>128</div>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center gap-3 flex-wrap">
                    <Link
                      to="/projects/biopintostp"
                      data-testid="featured-biopinto-cta"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-[11px] uppercase tracking-[0.2em] transition-all hover:translate-y-[-2px]"
                      style={{ background: NEON, color: "#0A0F1A", boxShadow: `0 0 24px -4px ${NEON}` }}
                    >
                      {dt.common.open_dashboard} <ArrowRight size={13} strokeWidth={2.4} />
                    </Link>
                    <Link
                      to="/contact"
                      className="text-[11px] uppercase tracking-[0.22em] text-hx-muted hover:text-hx-neon transition-colors inline-flex items-center gap-2"
                    >
                      {dt.common.request_demo} <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>

                {/* Right: preview panel */}
                <div className="relative p-6 lg:p-8 lg:border-l" style={{ borderColor: `${NEON}18`, background: "rgba(6,10,18,0.4)" }}>
                  <div className="text-[9px] uppercase tracking-[0.28em] text-hx-muted font-mono mb-4">{dt.common.preview_telemetry}</div>

                  {/* Live feed simulation */}
                  <ul className="space-y-3">
                    {[
                      { c: CYAN, lbl: "SAT · CropWatch", val: "NDVI 0.74" },
                      { c: SOLAR, lbl: "Solar FusionSolar", val: "312 kWh · autoconsumo" },
                      { c: NEON, lbl: "Aviário A1", val: "3.400 un · +2%" },
                      { c: NEON, lbl: "Setor C2 Hortícolas", val: "Humidade 72% · ideal" },
                      { c: CYAN, lbl: "Bombagem DIFFUL", val: "Reservatório 87%" },
                    ].map((row, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: 8 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.1 + i * 0.07 }}
                        className="flex items-center justify-between gap-3 rounded-md px-3 py-2.5 border"
                        style={{ background: `${row.c}08`, borderColor: `${row.c}20` }}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0 animate-pulse" style={{ background: row.c, boxShadow: `0 0 8px ${row.c}` }} />
                          <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-hx-muted truncate">{row.lbl}</span>
                        </div>
                        <span className="text-[11px] font-mono font-semibold tabular-nums shrink-0" style={{ color: row.c }}>{row.val}</span>
                      </motion.li>
                    ))}
                  </ul>

                  <div className="mt-6 pt-5 border-t border-white/[0.10] text-[9px] uppercase tracking-[0.24em] text-hx-muted/70 font-mono">
                    Atualização · 6 passagens satélite/dia · 128 nós IoT
                  </div>
                </div>
              </div>
            </motion.article>

            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">

              {/* ============================================================ */}
              {/*  FEATURED SECONDARY: HX Cabo Verde Hub                        */}
              {/* ============================================================ */}
              <motion.article
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="md:col-span-2 relative rounded-2xl border overflow-hidden"
                style={{ borderColor: `${SOLAR}40`, boxShadow: `0 0 60px -20px ${SOLAR}44` }}
                data-testid="featured-cv-hub"
              >
                {/* Background image */}
                <div className="absolute inset-0 -z-0">
                  <img src="/hanno/cabo-verde/cv-hero.png" alt="Hub energético insular HX em Cabo Verde" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,15,26,0.85) 0%, rgba(10,15,26,0.85) 45%, rgba(10,15,26,0.85) 100%)" }} />
                </div>

                {/* Ambient glow */}
                <div className="pointer-events-none absolute inset-0 opacity-60">
                  <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl" style={{ background: `${SOLAR}22` }} />
                  <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full blur-3xl" style={{ background: `${NEON}22` }} />
                </div>

                <div className="relative grid lg:grid-cols-[1.15fr_1fr] gap-0">
                  <div className="p-8 lg:p-10">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ background: `${NEON}10`, borderColor: `${NEON}44` }}>
                        <span className="relative flex items-center justify-center w-2 h-2">
                          <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: NEON }} />
                          <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: NEON }} />
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: NEON }}>
                          {dt.cv.featured_status}
                        </span>
                      </div>
                      <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded border" style={{ background: `${SOLAR}0d`, borderColor: "#F59E0B44" }}>
                        <span style={{ color: SOLAR }} className="text-[9px] uppercase tracking-[0.24em] font-mono font-semibold">{dt.cv.featured_tag}</span>
                      </div>
                    </div>

                    <div className="mt-6 flex items-center gap-3">
                      <CountryFlag slug="cabo-verde" className="w-11 h-8 rounded shadow-lg" />
                      <div className="text-[10px] uppercase tracking-[0.28em] text-hx-muted font-mono">
                        {dt.cv.country_label}
                      </div>
                    </div>

                    <h3 className="mt-4 font-display font-bold text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.05]">
                      {dt.cv.title_a} <span style={{ color: SOLAR }}>—</span> {dt.cv.title_b}
                    </h3>

                    <p className="mt-5 text-[14px] lg:text-[15px] text-hx-muted leading-relaxed max-w-xl">
                      {dt.cv.featured_lede}
                    </p>

                    <div className="mt-8 grid grid-cols-3 gap-3">
                      <div className="rounded-lg px-3 py-3 border" style={{ background: `${SOLAR}08`, borderColor: `${SOLAR}22` }}>
                        <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted font-mono">Solar</div>
                        <div className="mt-0.5 font-display font-bold text-lg tabular-nums" style={{ color: SOLAR }}>12.4 MWp</div>
                      </div>
                      <div className="rounded-lg px-3 py-3 border" style={{ background: `${NEON}08`, borderColor: `${NEON}22` }}>
                        <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted font-mono">BESS</div>
                        <div className="mt-0.5 font-display font-bold text-lg tabular-nums" style={{ color: NEON }}>8 MWh</div>
                      </div>
                      <div className="rounded-lg px-3 py-3 border" style={{ background: `${CYAN}08`, borderColor: `${CYAN}22` }}>
                        <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted font-mono">Uptime</div>
                        <div className="mt-0.5 font-display font-bold text-lg tabular-nums" style={{ color: CYAN }}>99.94%</div>
                      </div>
                    </div>

                    <div className="mt-8 flex items-center gap-3 flex-wrap">
                      <Link
                        to="/projects/cabo-verde-hub"
                        data-testid="featured-cv-cta"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-[11px] uppercase tracking-[0.2em] transition-all hover:translate-y-[-2px]"
                        style={{ background: SOLAR, color: "#0A0F1A", boxShadow: `0 0 24px -4px ${SOLAR}` }}
                      >
                        {dt.common.open_dashboard} <ArrowRight size={13} strokeWidth={2.4} />
                      </Link>
                      <Link to="/contact" className="text-[11px] uppercase tracking-[0.22em] text-hx-muted hover:text-hx-neon transition-colors inline-flex items-center gap-2">
                        {dt.common.request_demo} <ArrowRight size={12} />
                      </Link>
                    </div>
                  </div>

                  {/* Right: live grid feed */}
                  <div className="relative p-6 lg:p-8 lg:border-l" style={{ borderColor: `${SOLAR}18`, background: "rgba(6,10,18,0.4)" }}>
                    <div className="text-[9px] uppercase tracking-[0.28em] text-hx-muted font-mono mb-4">{dt.common.grid_telemetry}</div>
                    <ul className="space-y-3">
                      {[
                        { c: SOLAR, lbl: "Solar Santiago", val: "9.2 MW" },
                        { c: NEON,  lbl: "BESS SoC médio", val: "72 % · 4h reserva" },
                        { c: CYAN,  lbl: "Grid Frequência", val: "50.00 Hz" },
                        { c: CYAN,  lbl: "Rede EV · Praia", val: "24 sessões ativas" },
                        { c: SOLAR, lbl: "Eólica Sal WT-04", val: "2.4 MW · V90" },
                      ].map((row, i) => (
                        <motion.li key={i} initial={{ opacity: 0, x: 8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 + i * 0.07 }}
                          className="flex items-center justify-between gap-3 rounded-md px-3 py-2.5 border"
                          style={{ background: `${row.c}08`, borderColor: `${row.c}20` }}>
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="w-1.5 h-1.5 rounded-full shrink-0 animate-pulse" style={{ background: row.c, boxShadow: `0 0 8px ${row.c}` }} />
                            <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-hx-muted truncate">{row.lbl}</span>
                          </div>
                          <span className="text-[11px] font-mono font-semibold tabular-nums shrink-0" style={{ color: row.c }}>{row.val}</span>
                        </motion.li>
                      ))}
                    </ul>
                    <div className="mt-6 pt-5 border-t border-white/[0.10] text-[9px] uppercase tracking-[0.24em] text-hx-muted/70 font-mono">
                      {dt.cv.preview_footer}
                    </div>
                  </div>
                </div>
              </motion.article>
              {STRATEGIC_PROJECTS.map((p, i) => {
                const t = c.projects.list[p.slug];
                return (
                <motion.article
                  key={p.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="relative rounded-2xl border overflow-hidden"
                  style={{ borderColor: `${NEON}22`, background: "linear-gradient(180deg, rgba(22,32,51,0.7) 0%, rgba(10,15,26,0.85) 100%)" }}
                  data-testid={`project-detail-${p.slug}`}
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={p.image} alt={t.country} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.85) 0%, rgba(10,15,26,0.85) 60%, rgba(10,15,26,0.85) 100%)" }} />
                    <div className="absolute bottom-5 left-6 flex items-center gap-4">
                      <CountryFlag slug={p.slug} className="w-14 h-10 rounded shadow-2xl" />
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-hx-neon font-mono font-semibold">{t.focus}</div>
                        <div className="mt-1 font-display font-semibold text-3xl lg:text-4xl tracking-tight text-hx-text" style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}>{t.country}</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-8">
                    <p className="font-display text-lg lg:text-xl text-hx-text/90 leading-snug">{t.tagline}</p>
                    <p className="mt-4 text-[14px] text-hx-muted leading-relaxed">{t.description}</p>
                    <div className="mt-8">
                      <div className="text-[10px] uppercase tracking-[0.26em] text-hx-muted/70 font-mono mb-4">{c.projects.components}</div>
                      <ul className="grid grid-cols-1 gap-2">
                        {t.highlights.map((h) => (
                          <li key={h} className="flex items-center gap-3 rounded-lg px-4 py-3 border" style={{ background: `${NEON}08`, borderColor: `${NEON}22` }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: NEON, boxShadow: `0 0 8px ${NEON}` }} />
                            <span className="text-[13.5px] text-hx-text/90 font-medium">{h}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-8 pt-6 border-t border-white/[0.10] grid grid-cols-3 gap-4">
                      <div>
                        <MapPin size={12} style={{ color: NEON }} className="mb-1" />
                        <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted font-mono">{c.projects.location}</div>
                        <div className="mt-1 text-[13px] text-hx-text/90 font-medium">{t.country}</div>
                      </div>
                      <div>
                        <Calendar size={12} style={{ color: NEON }} className="mb-1" />
                        <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted font-mono">{c.projects.start}</div>
                        <div className="mt-1 text-[13px] text-hx-text/90 font-medium">2027–2028</div>
                      </div>
                      <div>
                        <Users size={12} style={{ color: NEON }} className="mb-1" />
                        <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted font-mono">{c.projects.jobs}</div>
                        <div className="mt-1 text-[13px] text-hx-text/90 font-medium">100+</div>
                      </div>
                    </div>
                    {p.link && (
                      <Link to={p.link}
                        className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.18em] font-semibold transition-all hover:translate-y-[-1.5px]"
                        style={{ background: NEON, color: "#0A0F1A", boxShadow: `0 0 24px -4px ${NEON}` }}
                        data-testid={`project-link-${p.slug}`}>
                        {t.linkLabel || p.linkLabel || "Explore"} <ArrowRight size={13} strokeWidth={2.4} />
                      </Link>
                    )}
                  </div>
                </motion.article>
                );
              })}
            </div>
            <div className="mt-12 rounded-2xl border border-dashed p-8 text-center" style={{ borderColor: `${NEON}44` }}>
              <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold mb-3">{c.projects.next_markets}</div>
              <div className="font-display font-semibold text-2xl lg:text-3xl tracking-tight text-hx-text">
                🇦🇴 Angola · 🇲🇿 Moçambique · 🇬🇼 Guiné-Bissau · 🇹🇱 Timor-Leste
              </div>
              <p className="mt-4 text-[13.5px] text-hx-muted max-w-2xl mx-auto">
                {c.projects.next_markets_desc}
              </p>
            </div>
          </div>
        </section>
        <section className="relative py-16 lg:py-20 border-t border-white/[0.10]">
          <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
            <h3 className="font-display font-semibold text-3xl lg:text-4xl tracking-tight leading-[1.1]">
              {c.projects.cta_title_p1} <span className="text-hx-neon">{c.projects.cta_title_accent}</span>
            </h3>
            <p className="mt-4 text-[14px] text-hx-muted leading-relaxed max-w-2xl mx-auto">
              {c.projects.cta_lede}
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-hx-neon text-hx-bg font-semibold text-[11px] uppercase tracking-[0.18em] transition-all hover:translate-y-[-2px]"
              style={{ boxShadow: `0 0 24px -4px ${NEON}` }}
            >
              {c.projects.cta_button} <ArrowRight size={13} strokeWidth={2.4} />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
